// The ONLY public roots a marketing asset slot may resolve. VIS-4 (2026-08-18) removed
// "/marketing/feature-icons/": that family was a build-time normalization source with zero runtime
// request paths and now lives under docs/audit/marketing-site/module-identity-sources/, so keeping
// it approved here would bless a root that no longer resolves.
const APPROVED_ASSET_ROOTS = Object.freeze([
  "/marketing/mobile/product-ui/v1/",
]);

export const VERIFIED_RELEASE_STATUSES = Object.freeze([
  "verified-first-party",
  "verified-generated-commercial-rights",
  "verified-third-party-license",
]);

// Historical mobile captures remain available to the audit registry, but public runtime resolution
// is deliberately disabled. Keep these records so old keys fail closed instead of silently
// requesting a file that has been moved out of frontend/public.
const releasedFirstPartyAsset = ({ key, assetId, kind, width, height, alt = "" }) => ({
  key,
  assetId,
  kind,
  width,
  height,
  alt,
  lifecycle: "quarantined",
  releaseStatus: "internal-provenance-only",
  exports: [],
});

// VIS-9: each released capture also ships lossless WebP and AVIF q70 4:4:4 siblings, generated
// and checksummed by frontend/scripts/generate_mobile_product_ui_assets.py and registered in
// marketing/mobile-product-ui.manifest.json. MarketingAssetSlot already renders <picture>, so
// listing them here is what actually puts AVIF/WebP on the wire; the PNG stays the <img>
// fallback and the reviewed master. All three are the SAME 402x874 pixels — the captures render
// at a maximum of 201 CSS px, so the native capture is the 2x rung.
const publicMobileAsset = ({ key, assetId, width, height, alt, publicPath, sha256, webp, avif }) => ({
  key,
  assetId,
  kind: "mobile-product-screen",
  width,
  height,
  alt,
  lifecycle: "public-marketing",
  releaseStatus: "verified-first-party",
  exports: [
    { format: "avif", width, height, publicPath: avif.publicPath, sha256: avif.sha256 },
    { format: "webp", width, height, publicPath: webp.publicPath, sha256: webp.sha256 },
    { format: "png", width, height, publicPath, sha256 },
  ],
});

const excludedAsset = ({ key, assetId, kind, width, height }) => ({
  key,
  assetId,
  kind,
  width,
  height,
  alt: "",
  lifecycle: "cancelled",
  releaseStatus: "excluded",
  exports: [],
});

// Runtime projection of marketing/assets.manifest.json. Five independently reviewed,
// portrait-free captures may resolve on public marketing surfaces. Every other native capture
// remains audit-only and intentionally exposes no public path.
export const MOBILE_MARKETING_ASSETS = Object.freeze({
  // Compatibility-only records. The icon-only art direction cancelled both
  // generated hero concepts; they can never resolve to a runtime request.
  heroSchoolEcosystem: excludedAsset({
    key: "heroSchoolEcosystem",
    assetId: "vp360-hero-school-ecosystem-v1",
    kind: "hero",
    width: 1600,
    height: 1200,
  }),
  heroMobilePersonas: excludedAsset({
    key: "heroMobilePersonas",
    assetId: "vp360-hero-mobile-personas-v1",
    kind: "persona-hero",
    width: 1400,
    height: 1120,
  }),
  // This legacy key pointed to a byte-identical copy of the teacher home screen.
  // Keep it for manifest compatibility, but never present it as separate product proof.
  spotClassroom: excludedAsset({
    key: "spotClassroom",
    assetId: "vp360-spot-classroom-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
  }),
  spotAttendance: releasedFirstPartyAsset({
    key: "spotAttendance",
    assetId: "vp360-spot-attendance-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Attendance term summary and recent-day records in the mobile app.",
  }),
  spotFees: releasedFirstPartyAsset({
    key: "spotFees",
    assetId: "vp360-spot-fees-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Fees, invoices and payment history in the mobile app.",
  }),
  spotReportCard: releasedFirstPartyAsset({
    key: "spotReportCard",
    assetId: "vp360-spot-report-card-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Published results and subject performance in the mobile app.",
  }),
  spotTimetable: releasedFirstPartyAsset({
    key: "spotTimetable",
    assetId: "vp360-spot-timetable-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Student day timetable in the mobile app.",
  }),
  spotBusTracking: releasedFirstPartyAsset({
    key: "spotBusTracking",
    assetId: "vp360-spot-bus-tracking-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "School bus route and live trip status in the mobile app.",
  }),
  screenStudentHome: releasedFirstPartyAsset({
    key: "screenStudentHome",
    assetId: "vp360-screen-student-home-light-v1",
    kind: "persona-screen",
    width: 402,
    height: 874,
    alt: "Student mobile app home screen.",
  }),
  screenGuardianHome: releasedFirstPartyAsset({
    key: "screenGuardianHome",
    assetId: "vp360-screen-guardian-home-light-v1",
    kind: "persona-screen",
    width: 402,
    height: 874,
    alt: "Guardian mobile app home screen.",
  }),
  screenTeacherHome: releasedFirstPartyAsset({
    key: "screenTeacherHome",
    assetId: "vp360-screen-teacher-home-light-v1",
    kind: "persona-screen",
    width: 402,
    height: 874,
    alt: "Teacher mobile app home screen.",
  }),
  screenAdminHome: releasedFirstPartyAsset({
    key: "screenAdminHome",
    assetId: "vp360-screen-admin-home-light-v1",
    kind: "persona-screen",
    width: 402,
    height: 874,
    alt: "Administrator mobile app home screen.",
  }),
  screenHomework: releasedFirstPartyAsset({
    key: "screenHomework",
    assetId: "vp360-screen-student-homework-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Student homework list in the Vidyapeeth360 mobile app.",
  }),
  screenNotices: publicMobileAsset({
    key: "screenNotices",
    assetId: "vp360-screen-student-notices-light-v1",
    width: 402,
    height: 874,
    alt: "School notices in the Vidyapeeth360 mobile app.",
    publicPath: "/marketing/mobile/product-ui/v1/school-notices.png",
    sha256: "c67f49190fc5631814b43e2a02e1a0c6c677bfd56a4cf1f5a30fbeb597cbed16",
    webp: { publicPath: "/marketing/mobile/product-ui/v1/school-notices.webp", sha256: "47f159827007e611ae79929112985ef9d0d633aa1bf8ee49fdeca694b83d3003" },
    avif: { publicPath: "/marketing/mobile/product-ui/v1/school-notices.avif", sha256: "08516eca4f8ac5c609ec52e93e76a210a4d1054d35c74ec47c22520b9c42d7b7" },
  }),
  screenChat: releasedFirstPartyAsset({
    key: "screenChat",
    assetId: "vp360-screen-student-chat-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Class chat in the Vidyapeeth360 mobile app.",
  }),
  screenCalendar: publicMobileAsset({
    key: "screenCalendar",
    assetId: "vp360-screen-student-calendar-light-v1",
    width: 402,
    height: 874,
    alt: "School calendar in the Vidyapeeth360 mobile app.",
    publicPath: "/marketing/mobile/product-ui/v1/school-calendar.png",
    sha256: "3ba6728512198baa2e471fa5acc5977726c978803c9d500fdf18501d11d48159",
    webp: { publicPath: "/marketing/mobile/product-ui/v1/school-calendar.webp", sha256: "7ab420aa4ce31d0e1e7d6fa51166421a4d7f934c3424acd90432c4f7bf81b169" },
    avif: { publicPath: "/marketing/mobile/product-ui/v1/school-calendar.avif", sha256: "d6bbdcfa69a2a3a56c42d77ab3df91648698594f3a1e8d16e1b9585b22c1daf8" },
  }),
  screenForms: releasedFirstPartyAsset({
    key: "screenForms",
    assetId: "vp360-screen-student-forms-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "School forms in the Vidyapeeth360 mobile app.",
  }),
  screenAchievements: releasedFirstPartyAsset({
    key: "screenAchievements",
    assetId: "vp360-screen-student-achievements-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Student achievements in the Vidyapeeth360 mobile app.",
  }),
  screenCertificates: releasedFirstPartyAsset({
    key: "screenCertificates",
    assetId: "vp360-screen-student-certificates-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Student certificates in the Vidyapeeth360 mobile app.",
  }),
  screenProfile: releasedFirstPartyAsset({
    key: "screenProfile",
    assetId: "vp360-screen-student-profile-light-v1",
    kind: "module-screen",
    width: 402,
    height: 874,
    alt: "Student profile and support settings in the Vidyapeeth360 mobile app.",
  }),
  screenAira: publicMobileAsset({
    key: "screenAira",
    assetId: "vp360-screen-parent-aira-light-v1",
    width: 402,
    height: 874,
    alt: "Aira assistant in the Vidyapeeth360 mobile app.",
    publicPath: "/marketing/mobile/product-ui/v1/aira-assistant.png",
    sha256: "b20b07bc48aeedf39126a6f2e15f9f91a02d662780319115fb44ecd7ed8c7c59",
    webp: { publicPath: "/marketing/mobile/product-ui/v1/aira-assistant.webp", sha256: "2218500ff0288cdd146df617fdafaa02a166205967faeb10375c564789439079" },
    avif: { publicPath: "/marketing/mobile/product-ui/v1/aira-assistant.avif", sha256: "59f8c841afc3c6434896f0bc637400d9b662dc97007fa38497f48b1fdf2c8b4b" },
  }),
  screenTaskDetail: publicMobileAsset({
    key: "screenTaskDetail",
    assetId: "vp360-screen-staff-task-detail-light-v1",
    width: 402,
    height: 874,
    alt: "Operations task checklist and proof controls in the mobile app.",
    publicPath: "/marketing/mobile/product-ui/v1/operations-task.png",
    sha256: "4f22f1c9f1f554942b3469529c3e972188d734647cbda1c0d5bc23f68aed6828",
    webp: { publicPath: "/marketing/mobile/product-ui/v1/operations-task.webp", sha256: "48c8dd341f86b832897b0489f730d015722a8c4cbdb47d83e7b0142af5c5aa6e" },
    avif: { publicPath: "/marketing/mobile/product-ui/v1/operations-task.avif", sha256: "43eb5b978dee2475cea447660e779fd5c6146957babad13e75fccac01a4eeb93" },
  }),
  screenSecurityOverview: publicMobileAsset({
    key: "screenSecurityOverview",
    assetId: "vp360-screen-account-security-overview-light-v1",
    width: 402,
    height: 874,
    alt: "Password, authenticator and notification device controls in the mobile app.",
    publicPath: "/marketing/mobile/product-ui/v1/account-security.png",
    sha256: "6d8613da3b370f57bc6388995d9d1579d2ba69db2f200e9e24bd0a354c9d9e5d",
    webp: { publicPath: "/marketing/mobile/product-ui/v1/account-security.webp", sha256: "b4de1fb873ea6f325bdb4f196ad8697a61b372becf909124c003052f58b38f12" },
    avif: { publicPath: "/marketing/mobile/product-ui/v1/account-security.avif", sha256: "31393b39274aa0767e3d44c04efaa47b0567644be848245e105b04e60ac463e3" },
  }),
});

export function getMobileMarketingAsset(assetKey) {
  return MOBILE_MARKETING_ASSETS[assetKey] || null;
}

function isApprovedPublicPath(value) {
  if (typeof value !== "string") return false;
  const root = APPROVED_ASSET_ROOTS.find((candidate) => value.startsWith(candidate));
  if (!root) return false;
  const filename = value.slice(root.length);
  return /^[a-z0-9][a-z0-9-]*\.(?:avif|webp|png)$/.test(filename);
}

export function resolveApprovedAssetSources(asset) {
  if (!asset || !VERIFIED_RELEASE_STATUSES.includes(asset.releaseStatus)) return null;
  if (asset.kind === "mobile-product-screen" && asset.lifecycle !== "public-marketing") return null;

  const exports = Array.isArray(asset.exports) ? asset.exports : [];
  const released = exports
    .filter((item) => item && isApprovedPublicPath(item.publicPath))
    .filter((item) => ["avif", "webp", "png"].includes(item.format))
    .sort((a, b) => Number(a.width || 0) - Number(b.width || 0));

  if (!released.length) return null;

  const srcSetFor = (format) => released
    .filter((item) => item.format === format)
    .map((item) => `${item.publicPath} ${item.width}w`)
    .join(", ");
  // The <img src> is the LAST resort, reached only by a browser that understood none of the
  // <source> types — so it must be the most universally decodable export, which is the PNG.
  // Picking WebP here would leave such a browser with no image at all, and relying on array
  // order is fragile now that every format is registered at the same width.
  const preferred = ["png", "webp", "avif"]
    .map((format) => released.find((item) => item.format === format))
    .find(Boolean);
  const fallback = preferred || released[released.length - 1];

  return {
    avifSrcSet: srcSetFor("avif"),
    webpSrcSet: srcSetFor("webp"),
    fallbackSrc: fallback.publicPath,
  };
}
